<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-person-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="customer-person-modal-title">{{ modalTitle }}</h3>
          <p>{{ modalSubtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.modal.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__body">
        <aside class="app-modal__menu" :aria-label="text.modal.formSectionsAriaLabel">
          <button
            v-for="section in sections"
            :key="section.key"
            class="app-modal__menu-item"
            :class="{ active: activeSectionKey === section.key }"
            type="button"
            @click="activeSectionKey = section.key"
          >
            <span class="app-modal__menu-icon">
              <i class="fa" :class="section.icon" aria-hidden="true"></i>
            </span>
            <span class="app-modal__menu-copy">
              <strong>{{ section.title }}</strong>
              <small>{{ section.description }}</small>
            </span>
          </button>
        </aside>

        <div class="app-modal__content">
          <form class="app-modal__form" @submit.prevent="submitForm">
            <PersonSectionBasic
              :active="activeSectionKey === 'basic'"
              :form="form"
              :v$="v$"
            />

            <PersonSectionContact
              :active="activeSectionKey === 'contact'"
              :form="form"
              :v$="v$"
            />

            <PersonSectionRole
              :active="activeSectionKey === 'role'"
              :form="form"
            />

            <PersonSectionAddress
              :active="activeSectionKey === 'address'"
              :form="form"
              :country-options="normalizedCountryOptions"
              :state-options="filteredStateOptions"
            />

            <footer class="app-modal__footer">
              <div class="app-modal__footer-left">
                <button
                  v-if="isEditMode"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--delete person-modal__delete-btn"
                  :title="text.card.actions.delete"
                  :aria-label="text.card.actions.delete"
                  :disabled="loading"
                  @click="emitDelete"
                >
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>
              </div>
              <div class="app-modal__footer-right">
                <button type="button" class="btn btn-light" :disabled="loading" @click="emitClose">{{ text.modal.cancel }}</button>
                <button type="submit" class="btn btn-primary" :disabled="loading">{{ submitButtonLabel }}</button>
              </div>
            </footer>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { findOptionByRawValue, getOptionValue } from '@/utils/optionValues';
import PersonSectionBasic from '@/components/people/modal/PersonSections/PersonSectionBasic.vue';
import PersonSectionContact from '@/components/people/modal/PersonSections/PersonSectionContact.vue';
import PersonSectionRole from '@/components/people/modal/PersonSections/PersonSectionRole.vue';
import PersonSectionAddress from '@/components/people/modal/PersonSections/PersonSectionAddress.vue';
import {
  PERSON_SECTIONS,
  createInitialPersonForm,
} from '@/composables/people/personModalConfig';
import { useDomainText } from '@/composables/text/useDomainText';
import { toBooleanFlag } from '@/utils/dataNormalization';
import { useVuelidate } from '@vuelidate/core';
import { email, helpers } from '@vuelidate/validators';

const { catalog: text } = useDomainText('people');

export default {
  name: 'PersonModal',
  components: {
    PersonSectionBasic,
    PersonSectionContact,
    PersonSectionRole,
    PersonSectionAddress,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    initialData: {
      type: Object,
      default: null,
    },
    countryOptions: {
      type: Array,
      default: () => [],
    },
    stateOptions: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'submit', 'delete'],
  data() {
    return {
      text,
      sections: PERSON_SECTIONS,
      activeSectionKey: 'basic',
      form: createInitialPersonForm(),
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || '').trim().toLowerCase() === 'edit';
    },
    modalTitle() {
      return this.isEditMode ? this.text.modal.titleEdit : this.text.modal.titleCreate;
    },
    modalSubtitle() {
      return this.isEditMode
        ? this.text.modal.subtitleEdit
        : this.text.modal.subtitleCreate;
    },
    submitButtonLabel() {
      return this.isEditMode ? this.text.modal.submitEdit : this.text.modal.submitCreate;
    },
    normalizedCountryOptions() {
      return this.normalizeOptions(this.countryOptions);
    },
    normalizedStateOptions() {
      return this.normalizeOptions(this.stateOptions);
    },
    filteredStateOptions() {
      const selectedCountry = this.form.country;
      if (!selectedCountry) {
        return this.normalizedStateOptions;
      }

      const selectedCountryValue = String(selectedCountry.value || '').toLowerCase();
      const selectedCountryCode = String(selectedCountry.meta?.code || '').toLowerCase();
      const selectedCountryName = String(selectedCountry.meta?.name || '').toLowerCase();

      const filtered = this.normalizedStateOptions.filter((option) => {
        const optionCountryId = String(option.meta?.country_id ?? option.meta?.countryId ?? '').toLowerCase();
        const optionCountryCode = String(option.meta?.country_code ?? option.meta?.countryCode ?? '').toLowerCase();
        const optionCountryName = String(option.meta?.country_name ?? option.meta?.countryName ?? '').toLowerCase();

        if (!optionCountryId && !optionCountryCode && !optionCountryName) {
          return true;
        }

        return [optionCountryId, optionCountryCode, optionCountryName].includes(selectedCountryValue)
          || [optionCountryId, optionCountryCode, optionCountryName].includes(selectedCountryCode)
          || [optionCountryId, optionCountryCode, optionCountryName].includes(selectedCountryName);
      });

      return filtered.length > 0 ? filtered : this.normalizedStateOptions;
    },
  },
  validations() {
    return {
      form: {
        first_name: {
          required: helpers.withMessage(
            this.text.modal.validation.firstNameRequired,
            (value) => helpers.req(String(value || '').trim())
          ),
        },
        email: {
          emailFormat: helpers.withMessage(
            this.text.modal.validation.emailInvalid,
            (value) => {
              const normalized = String(value || '').trim();

              if (!helpers.req(normalized)) {
                return true;
              }

              return email.$validator ? email.$validator(normalized) : email(normalized);
            }
          ),
        },
      },
    };
  },
  setup() {
    const v$ = useVuelidate();

    return { v$ };
  },
  watch: {
    show(isVisible) {
      if (isVisible) {
        this.activeSectionKey = this.sections[0].key;
        this.form = this.buildFormFromInitialData();
        this.v$.$reset();
        return;
      }

      this.form = createInitialPersonForm();
      this.activeSectionKey = this.sections[0].key;
      this.v$.$reset();
    },
    initialData() {
      if (!this.show) {
        return;
      }

      this.form = this.buildFormFromInitialData();
      this.v$.$reset();
    },
    'form.country'() {
      const selectedStateValue = String(getOptionValue(this.form.state) || '').trim();
      const isStateAllowed = this.filteredStateOptions.some((option) => String(option?.value || '') === selectedStateValue);

      if (!isStateAllowed) {
        this.form.state = null;
      }
    },
  },
  methods: {
    normalizeOptions(options) {
      return normalizeCatalogOptions(options);
    },
    emitClose() {
      this.$emit('close');
    },
    emitDelete() {
      this.$emit('delete', this.initialData || null);
    },
    buildFormFromInitialData() {
      const initial = this.initialData || {};

      const countryOption = findOptionByRawValue(this.normalizedCountryOptions, initial?.country);
      const stateOption = findOptionByRawValue(this.normalizedStateOptions, initial?.state);

      return {
        ...createInitialPersonForm(),
        first_name: String(initial?.first_name || '').trim(),
        last_name: String(initial?.last_name || '').trim(),
        job_title: String(initial?.job_title || '').trim(),
        birthdate: String(initial?.birthdate || '').trim(),
        email: String(initial?.email || '').trim(),
        cell_phone: String(initial?.cell_phone || '').trim(),
        office_phone: String(initial?.office_phone || '').trim(),
        office_phone_ext: String(initial?.office_phone_ext || '').trim(),
        home_phone: String(initial?.home_phone || '').trim(),
        fax: String(initial?.fax || '').trim(),
        invoice_mailing: toBooleanFlag(initial?.invoice_mailing),
        payments_mailing: toBooleanFlag(initial?.payments_mailing),
        is_debt_contact: toBooleanFlag(initial?.is_debt_contact),
        default: toBooleanFlag(initial?.default),
        address: String(initial?.address || '').trim(),
        country: countryOption || null,
        state: stateOption || null,
        city: String(initial?.city || '').trim(),
        zipcode: String(initial?.zipcode || '').trim(),
      };
    },
    resolveGeoPayloadValue(option) {
      if (!option) {
        return '';
      }

      return String(option?.meta?.code || option?.meta?.name || option?.value || '').trim();
    },
    submitForm() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        if (this.v$.form.first_name.$error) {
          this.activeSectionKey = 'basic';
        } else if (this.v$.form.email.$error) {
          this.activeSectionKey = 'contact';
        }

        return;
      }

      const firstName = String(this.form.first_name || '').trim();
      const emailValue = String(this.form.email || '').trim();

      const selectedCountry = findOptionByRawValue(this.normalizedCountryOptions, this.form.country);
      const selectedState = findOptionByRawValue(this.normalizedStateOptions, this.form.state);

      const payload = {
        first_name: firstName,
        last_name: String(this.form.last_name || '').trim(),
        job_title: String(this.form.job_title || '').trim(),
        birthdate: this.form.birthdate || null,
        email: emailValue,
        cell_phone: String(this.form.cell_phone || '').trim(),
        office_phone: String(this.form.office_phone || '').trim(),
        office_phone_ext: String(this.form.office_phone_ext || '').trim(),
        home_phone: String(this.form.home_phone || '').trim(),
        fax: String(this.form.fax || '').trim(),
        invoice_mailing: this.form.invoice_mailing ? 1 : 0,
        payments_mailing: this.form.payments_mailing ? 1 : 0,
        is_debt_contact: this.form.is_debt_contact ? 1 : 0,
        default: this.form.default ? 1 : 0,
        address: String(this.form.address || '').trim(),
        country: this.resolveGeoPayloadValue(selectedCountry),
        state: this.resolveGeoPayloadValue(selectedState),
        city: String(this.form.city || '').trim(),
        zipcode: String(this.form.zipcode || '').trim(),
      };

      this.$emit('submit', payload);
    },
  },
};
</script>

<style scoped>
.app-modal__footer-left {
  margin-right: auto;
}

.person-modal__delete-btn {
  min-height: 38px;
  height: 38px;
  width: 44px;
  min-width: 44px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
